"use client";

import "@/components/bloks/defineBloks";
import { CentraProvider } from "@frend-digital/centra/client";
import { StoryblokProvider } from "@frend-digital/storyblok";
import { UIProvider } from "@frend-digital/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HeroProvider } from "@/components/bloks/sections/Hero/HeroContext";
import { SnackbarProvider } from "@/components/ui/Snackbar/SnackbarProvider";
import env from "@/env.mjs";
import { CentraCheckoutScript } from "@/features/checkout/CentraCheckout";
import Events from "@/features/global/Events";
import { useRouter } from "@/i18n/routing";
import { WishlistProvider } from "@/lib/centra/hooks";
import { KlaviyoIdentify } from "@/lib/klaviyo/components";
import "core-js/features/array/to-sorted";
import { useSearchParams } from "next/navigation";
import type { PropsWithChildren } from "react";
import { startTransition, useCallback, useState } from "react";

export default function Providers({ children }: PropsWithChildren) {
  const [queryClient] = useState(() => new QueryClient());

  const router = useRouter();

  const handlePublish = useCallback(() => {
    startTransition(() => {
      router.refresh();
    });

    if (queryClient.getQueriesData({}).length) {
      startTransition(async () => {
        await queryClient.invalidateQueries();
      });
    }
  }, [router, queryClient]);

  return (
    <UIProvider
      value={{
        navigation: {
          back: router.back.bind(router),
          push: router.push.bind(router),
          replace: router.replace.bind(router),
          useSearchParams,
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <CentraProvider apiUrl={env.NEXT_PUBLIC_CENTRA_CHECKOUT}>
          <StoryblokProvider onPublished={handlePublish}>
            <WishlistProvider>
              <SnackbarProvider>
                <HeroProvider>{children}</HeroProvider>
                <KlaviyoIdentify />
              </SnackbarProvider>
            </WishlistProvider>
            <CentraCheckoutScript />
            <ReactQueryDevtools initialIsOpen={false} />
            <Events />
          </StoryblokProvider>
        </CentraProvider>
      </QueryClientProvider>
    </UIProvider>
  );
}
